import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import type { StateCode } from "@cdl/shared-types";

const STORAGE_KEY = "cdl:session";

interface PersistedSession {
  userId: string; email: string; authToken: string;
  state: StateCode | null; zip: string | null;
  hasSeenOrientation?: boolean;
}

/**
 * Sync read for the web platform. Must be sync so the store is hydrated
 * before any router guard runs — otherwise a cold refresh at /home evaluates
 * `isAuthenticated` while still null and redirects to /welcome.
 */
function readSync(): PersistedSession | null {
  if (Capacitor.isNativePlatform()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedSession;
  } catch { return null; }
}

async function readAsync(): Promise<PersistedSession | null> {
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) return null;
    try { return JSON.parse(value) as PersistedSession; } catch { return null; }
  }
  return readSync();
}

async function writeAsync(s: PersistedSession): Promise<void> {
  const json = JSON.stringify(s);
  if (Capacitor.isNativePlatform()) {
    await Preferences.set({ key: STORAGE_KEY, value: json });
  } else {
    localStorage.setItem(STORAGE_KEY, json);
  }
}

async function clearAsync(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await Preferences.remove({ key: STORAGE_KEY });
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const useUserStore = defineStore("user", () => {
  // Seed refs synchronously on web so guards see the real auth state on
  // first evaluation, with no race against async hydration.
  const initial = readSync();

  const userId = ref<string | null>(initial?.userId ?? null);
  const email = ref<string | null>(initial?.email ?? null);
  const authToken = ref<string | null>(initial?.authToken ?? null);
  const state = ref<StateCode | null>(initial?.state ?? null);
  const zip = ref<string | null>(initial?.zip ?? null);
  const hasSeenOrientation = ref<boolean>(initial?.hasSeenOrientation ?? false);

  const isAuthenticated = computed(() => !!authToken.value);

  // Native platforms need async hydration. Web is already hydrated above.
  async function hydrate() {
    const s = await readAsync();
    if (!s) return;
    userId.value = s.userId;
    email.value = s.email;
    authToken.value = s.authToken;
    state.value = s.state;
    zip.value = s.zip;
    hasSeenOrientation.value = s.hasSeenOrientation ?? false;
  }

  async function setSession(s: { userId: string; email: string; authToken: string; state: StateCode; zip?: string | null }) {
    userId.value = s.userId;
    email.value = s.email;
    authToken.value = s.authToken;
    state.value = s.state;
    zip.value = s.zip ?? null;

    const persisted: PersistedSession = {
      userId: s.userId, email: s.email, authToken: s.authToken, state: s.state, zip: s.zip ?? null,
      hasSeenOrientation: hasSeenOrientation.value
    };
    await writeAsync(persisted);
  }

  async function markOrientationSeen() {
    hasSeenOrientation.value = true;
    if (!authToken.value) return;
    const persisted: PersistedSession = {
      userId: userId.value!, email: email.value!, authToken: authToken.value,
      state: state.value, zip: zip.value, hasSeenOrientation: true
    };
    await writeAsync(persisted);
  }

  async function clear() {
    userId.value = null; email.value = null; authToken.value = null;
    state.value = null; zip.value = null;
    hasSeenOrientation.value = false;
    await clearAsync();
  }

  return { userId, email, authToken, state, zip, hasSeenOrientation, isAuthenticated, hydrate, setSession, markOrientationSeen, clear };
});
